import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ClientDetailsSidebar from "../../components/siteCheck/ViewChecklistsDetails";
import EditChecklistSidebarWithToast from "../../components/siteCheck/EditCheckList";
import { useNavigate, useLocation } from "react-router-dom";
import ClientNotificationBanner from "../../components/ClientNotificationBanner";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { toast } from "react-hot-toast";

// Status styles mapping
const statusStyles = {
  UNASSIGNED: { bgcolor: "#F8D66D", color: "#fff" },
  ASSIGNED: { bgcolor: "#FFB54C", color: "#fff" },
  VISITED: { bgcolor: "#B3EBF2", color: "#fff" },
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff" },
  // FIR Statuses
  "REQUESTED TO ADMIN": { bgcolor: "#F8D66D", color: "#fff" },
  "PROJECT MAPPED": { bgcolor: "#FFB54C", color: "#fff" },
  "ASSIGNED ENGINEER": { bgcolor: "#B3EBF2", color: "#fff" },
  "COMPLETED INSPECTION": { bgcolor: "#8CD47E", color: "#fff" },
  "REPORT SUBMITTED": { bgcolor: "#8CD47E", color: "#fff" },
};

// Status display names
const statusDisplayNames = {
  UNASSIGNED: "Unassigned",
  ASSIGNED: "Assigned",
  VISITED: "Visited",
  SIGNPENDING: "Sign Pending",
  FINALREPORT: "Final Report",
  REJECTED: "Rejected",
  // FIR Statuses
  "REQUESTED TO ADMIN": "Requested to Admin",
  "PROJECT MAPPED": "Project Mapped",
  "ASSIGNED ENGINEER": "Assigned Engineer",
  "COMPLETED INSPECTION": "Completed Inspection",
  "REPORT SUBMITTED": "Report Submitted",
};

const AllProjects = () => {
  const { userData, isAuthenticated, hasRole, token, refreshUserData } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [pageSize] = useState(12);

  const [selectedProject, setSelectedProject] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditScreenOpen, setIsEditScreenOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasEditChanges, setHasEditChanges] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, status: null });

  const isIndividual = userData?.clientRole?.roleType === "Individual" || userData?.clientRole === "Individual";

  // Filter and sort states - Simplified sorting
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedSort, setSelectedSort] = useState("latest");
  const [sortConfig, setSortConfig] = useState({
    column: null, // 'projectName', 'clientName', 'visitDate'
    direction: null, // 'asc', 'desc'
  });



  // Add to your existing state declarations
  const [filterMenusOpen, setFilterMenusOpen] = useState({
    engineers: false,
    clients: false,
    templates: false,
    statuses: false,
  });
  const [downloadProgress, setDownloadProgress] = useState({});

  // Menu anchors - Removed individual sort menus
  const [sortAnchorEl, setSortAnchorEl] = useState(null); const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Date filter states
  const [dateFilter, setDateFilter] = useState("ALL");
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // New filter states
  const [engineers, setEngineers] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    engineers: false,
    clients: false,
    templates: false,
  });

  // Admin details state - for client role
  const [adminDetails, setAdminDetails] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);

  // Show filter section state
  const [showFilters, setShowFilters] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Fetch admin details for client role
  // Update the fetchAdminDetails function to get company details instead of admin details
  const fetchAdminDetails = useCallback(async () => {
    if (!hasRole("CLIENT") || !token) return;

    try {
      setAdminLoading(true);
      // Fetch users with ADMIN role
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "ADMIN",
          status: "APPROVED",
          limit: 1 // Get first admin
        }
      });

      if (response.data?.users && response.data.users.length > 0) {
        const admin = response.data.users[0];

        // Get company details from admin response (if available)
        const companyDetails = admin.companyDetails;

        // Use company phone if available, otherwise use admin phone as fallback
        const companyPhone = companyDetails?.phone || admin.phone || "+91 9999999999";
        const companyEmail = companyDetails?.email || admin.email || "company@example.com";
        const companyName = companyDetails?.name || admin.companyName || "Our Company";

        setAdminDetails({
          name: companyName, // Use company name instead of admin name
          phone: companyPhone,
          email: companyEmail,
          companyName: companyName,
          // Add company address if needed
          address: companyDetails?.address || "",
        });
      } else {
        // Set default company details if no admin found
        setAdminDetails({
          name: "Our Company",
          phone: "+91 9999999999",
          email: "company@example.com",
          companyName: "Our Company",
          address: "",
        });
      }
    } catch (err) {
      console.error("Error fetching admin details:", err);
      // Set default company details on error
      setAdminDetails({
        name: "Our Company",
        phone: "+91 9999999999",
        email: "company@example.com",
        companyName: "Our Company",
        address: "",
      });
    } finally {
      setAdminLoading(false);
    }
  }, [hasRole, token]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Wrap fetchProjects in useCallback to memoize it
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated || !token || !(hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Determine sort parameter based on active sort
      let sortParam = selectedSort;

      // If column sort is active, override with column-specific sort
      if (sortConfig.column && sortConfig.direction) {
        if (sortConfig.column === 'projectName') {
          sortParam = sortConfig.direction === 'asc' ? 'projectAz' : 'projectZa';
        } else if (sortConfig.column === 'clientName') {
          sortParam = sortConfig.direction === 'asc' ? 'clientAz' : 'clientZa';
        } else if (sortConfig.column === 'visitDate') {
          sortParam = sortConfig.direction === 'asc' ? 'visitDateAsc' : 'visitDateDesc';
        }
      }

      // Prepare date filter for visit date
      let visitDateFrom = null;
      let visitDateTo = null;

      if (dateFilter === "TODAY") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        visitDateFrom = today.toISOString();

        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        visitDateTo = todayEnd.toISOString();
      } else if (dateFilter === "YESTERDAY") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        visitDateFrom = yesterday.toISOString();

        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        visitDateTo = yesterdayEnd.toISOString();
      } else if (dateFilter === "TOMORROW") {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        visitDateFrom = tomorrow.toISOString();

        const tomorrowEnd = new Date(tomorrow);
        tomorrowEnd.setHours(23, 59, 59, 999);
        visitDateTo = tomorrowEnd.toISOString();
      } else if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
        const from = new Date(customDateRange.from);
        from.setHours(0, 0, 0, 0);
        visitDateFrom = from.toISOString();

        const to = new Date(customDateRange.to);
        to.setHours(23, 59, 59, 999);
        visitDateTo = to.toISOString();
      }

      // ENGINEER: Use different API endpoint
      if (hasRole("ENGINEER") && userData?._id) {
        const params = {
          page: page,
          limit: pageSize,
          sort: getSortParamForEngineer(),
        };

        // Add search query if exists
        if (searchDebounce.trim()) {
          params.search = searchDebounce.trim();
        }

        // ✅ FIXED: Add status filter (multiple or single)
        if (selectedStatuses.length > 0) {
          params.status = selectedStatuses.join(",");
        } else if (selectedStatus !== "ALL") {
          params.status = selectedStatus;
        }

        // ✅ FIXED: Calendar filters for engineers
        if (dateFilter !== "ALL") {
          let visitDateFrom = null;
          let visitDateTo = null;

          if (dateFilter === "TODAY") {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            visitDateFrom = today.toISOString();

            const todayEnd = new Date(today);
            todayEnd.setHours(23, 59, 59, 999);
            visitDateTo = todayEnd.toISOString();
          } else if (dateFilter === "YESTERDAY") {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            visitDateFrom = yesterday.toISOString();

            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);
            visitDateTo = yesterdayEnd.toISOString();
          } else if (dateFilter === "TOMORROW") {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);
            visitDateFrom = tomorrow.toISOString();

            const tomorrowEnd = new Date(tomorrow);
            tomorrowEnd.setHours(23, 59, 59, 999);
            visitDateTo = tomorrowEnd.toISOString();
          } else if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
            const from = new Date(customDateRange.from);
            from.setHours(0, 0, 0, 0);
            visitDateFrom = from.toISOString();

            const to = new Date(customDateRange.to);
            to.setHours(23, 59, 59, 999);
            visitDateTo = to.toISOString();
          }

          // ✅ IMPORTANT: Engineer API uses dateFrom/dateTo, not visitDateFrom/visitDateTo
          if (visitDateFrom) params.dateFrom = visitDateFrom;
          if (visitDateTo) params.dateTo = visitDateTo;
        }

        // Add other filters
        if (selectedClients.length > 0) {
          params.clientName = selectedClients.join(",");
        }

        if (selectedTemplates.length > 0) {
          params.siteCheckType = selectedTemplates.join(",");
        }

        const response = await axios.get(`${url}/api/engineer/assigned-projects/${userData._id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });
        const assignedProjects = response.data?.assignedProjects || [];
        const total = response.data?.total || 0;

        setProjects(transformProjects(assignedProjects));
        setTotalProjects(total);
        setTotalPages(Math.ceil(total / pageSize));

      } else {
        // CLIENT, ADMIN, SUPERADMIN: Use existing API
        const params = {
          page: page,
          limit: pageSize,
          sort: sortParam,
        };

        // Add search query if exists
        if (searchDebounce.trim()) {
          params.search = searchDebounce.trim();
        }

        // Only add clientID for CLIENT role
        if (hasRole("CLIENT") && userData?._id) {
          params.clientID = userData._id;
        }

        if (selectedStatus !== "ALL") {
          params.status = selectedStatus;
        }

        // Add multi-select filters
        if (selectedEngineers.length > 0) {
          params.engineerName = selectedEngineers.join(",");
        }

        if (selectedClients.length > 0) {
          params.clientName = selectedClients.join(",");
        }

        if (selectedTemplates.length > 0) {
          params.siteCheckType = selectedTemplates.join(",");
        }

        if (selectedStatuses.length > 0) {
          params.status = selectedStatuses.join(",");
        }

        // Add date filters for visit date
        if (visitDateFrom && visitDateTo) {
          params.visitDateFrom = visitDateFrom;
          params.visitDateTo = visitDateTo;
        }

        let endpoint = `${url}/api/client/requests`;
        if (isIndividual) {
          endpoint = `${url}/api/fir/requests`;
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        let allRequests = [];
        if (isIndividual) {
          allRequests = response.data?.data || [];
        } else {
          allRequests = response.data?.requests || [];
        }
        
        // Use allRequests.length if the total isn't returned from API
        const total = isIndividual ? allRequests.length : (response.data?.total || 0);

        setProjects(transformProjects(allRequests));
        setTotalProjects(total);
        setTotalPages(Math.ceil(total / pageSize) || 1);
      }

    } catch (err) {
      console.error("Error fetching projects:", err);
      setError(err.response?.data?.message || "Failed to load projects");
      setProjects([]);
      setTotalProjects(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated, token, hasRole, userData, page, pageSize,
    selectedSort, selectedStatus, sortConfig, dateFilter, customDateRange,
    selectedEngineers, selectedClients, selectedTemplates, selectedStatuses,
    searchDebounce, location.key // Add location.key to dependencies
  ]);

  // Main useEffect for fetching data
  useEffect(() => {
    if (isAuthenticated && (hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      fetchProjects();

      // Fetch admin details for client role
      if (hasRole("CLIENT")) {
        fetchAdminDetails();
      }

      if (showFilters) {
        fetchFilterData();
      }
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userData, token, page, selectedSort, selectedStatus, sortConfig,
    dateFilter, customDateRange, selectedEngineers, selectedClients,
    selectedTemplates, selectedStatuses, showFilters, searchDebounce,
    fetchProjects, fetchAdminDetails, hasRole, isAuthenticated, location.key
  ]);

  // Effect for handling page focus/visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible again
      if (!document.hidden) {
        console.log("Page became visible - refreshing data");
        fetchProjects();
      }
    };

    const handleFocus = () => {
      console.log("Window focused - refreshing data");
      fetchProjects();
    };

    // Add event listeners for both visibility and focus
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Clean up event listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchProjects]);

  // Add this helper function to your frontend component
  const getSortParamForEngineer = () => {
    if (sortConfig.column && sortConfig.direction) {
      if (sortConfig.column === 'projectName') {
        return sortConfig.direction === 'asc' ? 'projectAz' : 'projectZa';
      } else if (sortConfig.column === 'clientName') {
        return sortConfig.direction === 'asc' ? 'clientAz' : 'clientZa';
      } else if (sortConfig.column === 'visitDate') {
        return sortConfig.direction === 'asc' ? 'visitDateAsc' : 'visitDateDesc';
      }
    }

    // Default to latest if no column sort
    return selectedSort === "latest" ? "latest" : "oldest";
  };
  // Fetch filter data (engineers, clients, templates)
  const fetchFilterData = async () => {
    try {
      // Fetch engineers (only for ADMIN/SUPERADMIN)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN")) && !engineers.length) {
        setLoadingFilters(prev => ({ ...prev, engineers: true }));
        const engineersRes = await axios.get(`${url}/api/users/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: "ENGINEER", status: "APPROVED", sort: "az" }
        });
        setEngineers(engineersRes.data?.users || []);
        setLoadingFilters(prev => ({ ...prev, engineers: false }));
      }

      // Fetch clients (for ADMIN/SUPERADMIN and ENGINEER)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER")) && !clients.length) {
        setLoadingFilters(prev => ({ ...prev, clients: true }));
        const clientsRes = await axios.get(`${url}/api/users/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: "CLIENT", status: "APPROVED", sort: "az" }
        });
        setClients(clientsRes.data?.users || []);
        setLoadingFilters(prev => ({ ...prev, clients: false }));
      }

      // Fetch templates (for all roles)
      if (!templates.length) {
        setLoadingFilters(prev => ({ ...prev, templates: true }));
        const templatesRes = await axios.get(`${url}/api/admin/checklist-templates`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { isActive: true, limit: 100 }
        });
        setTemplates(templatesRes.data?.data || []);
        setLoadingFilters(prev => ({ ...prev, templates: false }));
      }

    } catch (err) {
      console.error("Error fetching filter data:", err);
    }
  };

  // Helper function to transform project data
  const transformProjects = (requests) => {
    return requests.map(item => {
      // For mapped FIR requests, the original FIR data is in item.firDetails
      const firData = item.firDetails || item;
      const isFirRequest = !!firData.propertyType && !!firData.fullName; // New FIR Request schema
      const isProjectModel = !!item.projectName && !isFirRequest;

      // Extract propertyType specifically to ensure it works for both mapped and unmapped
      const propertyType = firData.propertyType || "N/A";

      // Format Request Date
      let reqDateDisplay = "N/A";

      if (isFirRequest && firData.inspectionDateFrom) {
        const fromDate = new Date(firData.inspectionDateFrom);
        const toDate = firData.inspectionDateTo ? new Date(firData.inspectionDateTo) : null;
        
        if (!isNaN(fromDate)) {
          const fromFormatted = formatDate(fromDate);
          if (toDate && !isNaN(toDate)) {
            const toFormatted = formatDate(toDate);
            reqDateDisplay = fromDate.toDateString() === toDate.toDateString() ? fromFormatted : `${fromFormatted} - ${toFormatted}`;
          } else {
            reqDateDisplay = fromFormatted;
          }
        }
      }
      // 1️⃣ If preferredSiteVisitDate exists → use it
      else if (!isProjectModel && item.preferredSiteVisitDate?.fromDate) {
        const fromDate = new Date(item.preferredSiteVisitDate.fromDate);
        const toDate = item.preferredSiteVisitDate.toDate
          ? new Date(item.preferredSiteVisitDate.toDate)
          : null;

        if (!isNaN(fromDate)) {
          const fromFormatted = formatDate(fromDate);

          if (toDate && !isNaN(toDate)) {
            const toFormatted = formatDate(toDate);
            reqDateDisplay =
              fromDate.toDateString() === toDate.toDateString()
                ? fromFormatted
                : `${fromFormatted} - ${toFormatted}`;
          } else {
            reqDateDisplay = fromFormatted;
          }
        }
      }
      // 2️⃣ Else fallback to requestedDate or createdAt
      else if (item.requestedDate || item.createdAt) {
        const requested = new Date(item.requestedDate || item.createdAt);
        if (!isNaN(requested)) {
          reqDateDisplay = formatDate(requested);
        }
      }

      // Get status with role-based modifications
      let displayStatus = item.status || item.projectStatus || "UNKNOWN";
      if (displayStatus === "inprogress") displayStatus = "UNASSIGNED";

      // For CLIENT role, convert SIGNPENDING to VISITED
      if (hasRole("CLIENT") && displayStatus === "SIGNPENDING") {
        displayStatus = "VISITED";
      }

      if (item.source === "FIR" || isFirRequest) {
        displayStatus = item.firStatus || "REQUESTED TO ADMIN"; // Matches styles key
      }

      const hasReport = !!item.hasReport || (item.reportCount && item.reportCount > 0) || (item.fieldInspectionSummary && item.fieldInspectionSummary.totalReports > 0);
      const isCompleted = hasReport || item.firStatus === "Completed Inspection" || item.firStatus === "Report Submitted" || item.projectStatus === "completed" || item.status === "FINALREPORT";

      if (isCompleted) {
        displayStatus = "REPORT SUBMITTED";
      }

      // Format VISIT date - FIXED: Use visitedDate
      let visitedDateDisplay = "Not Scheduled";
      if (isIndividual) {
        if (isCompleted && item.updatedAt) {
          visitedDateDisplay = formatDate(new Date(item.updatedAt));
        } else {
          visitedDateDisplay = "Pending";
        }
      } else {
        if (item.assignedVisitDate) {
          try {
            let date = null;
            if (item.assignedVisitDate.$date) {
              date = new Date(item.assignedVisitDate.$date);
            } else if (item.assignedVisitDate instanceof Date || typeof item.assignedVisitDate === 'string') {
              date = new Date(item.assignedVisitDate);
            }

            if (date && !isNaN(date.getTime())) {
              visitedDateDisplay = formatDate(date);
            }
          } catch (error) {
            console.error("Error formatting visited date:", error);
          }
        }
      }

      // Get client name from different sources based on role
      let clientName = "N/A";
      let clientContact = "N/A";
      let clientAddress = "N/A";

      if (isFirRequest) {
        clientName = firData.fullName || "N/A";
        clientContact = firData.mobileNumber || "N/A";
        clientAddress = firData.fullAddress || "N/A";
      } else if (hasRole("ENGINEER")) {
        // Engineer API returns populated clientID
        clientName = item.clientID?.name || item.clientID?.companyName || item.nameOfClient || item.clientName || "N/A";
        clientContact = item.clientID?.phone || item.phoneOfClient || item.contactNumber || "N/A";
        clientAddress = item.detailedAddress || item.address || "N/A";
      } else {
        // Regular API for other roles
        clientName = item.nameOfClient || item.clientName || "N/A";
        clientContact = item.phoneOfClient || item.contactNumber || "N/A";
        clientAddress = item.detailedAddress || item.address || "N/A";
      }

      // Get engineer details — handle all possible API shapes
      let engineerName = "Not Assigned";
      let engineerContact = "";

      // DEBUG: log to inspect what the API actually returns
      if (hasRole("CLIENT")) {
        console.log('[Engineer Debug] item.assignedEngineer:', item.assignedEngineer, ' | item.assignedEngineers:', item.assignedEngineers, ' | status:', item.status);
      }

      const getEngineerFromField = (field) => {
        if (!field) return null;
        if (Array.isArray(field) && field.length > 0) {
          const names = field.map(e => (e && typeof e === 'object' && e.name) ? e.name : null).filter(Boolean);
          const phones = field.map(e => (e && typeof e === 'object' && e.phone) ? e.phone : '').filter(Boolean);
          if (names.length > 0) {
            engineerContact = phones.join(', ');
            return names.join(', ');
          }
        }
        if (typeof field === 'object' && field !== null && !Array.isArray(field)) {
          if (field.name) {
            engineerContact = field.phone || '';
            return field.name;
          }
        }
        return null;
      };

      // Try all possible field names in priority order
      const fromEngineers = getEngineerFromField(item.assignedEngineers);
      const fromEngineer = getEngineerFromField(item.assignedEngineer);

      if (fromEngineers) {
        engineerName = fromEngineers;
      } else if (fromEngineer) {
        engineerName = fromEngineer;
      }

      // Fix: If status says ASSIGNED but no engineer is actually assigned,
      // revert to UNASSIGNED so the display is accurate (only for non-FIR requests).
      if (
        displayStatus === "ASSIGNED" &&
        engineerName === "Not Assigned" &&
        !(item.source === "FIR" || isFirRequest)
      ) {
        displayStatus = "UNASSIGNED";
      }

      // Get project report number - handle missing data with dash
      const projectReportNo = item.projectReportNo || "";

      return {
        id: item._id || item.id,
        projectReportNo: projectReportNo,
        projectName: isFirRequest ? (firData.propertyName || firData.buildingName || firData.projectName || "FIR Project") : (isProjectModel ? item.projectName : (item.nameOfProject || "N/A")),
        clientName: clientName,
        clientContact: clientContact,
        clientAddress: clientAddress,
        siteCheck: (hasRole("CLIENT") && isIndividual) ? propertyType : (isFirRequest ? `FIR Request (${propertyType})` : (isProjectModel ? (item.towers?.length > 0 ? `FIR Request (${item.towers[0].projectType})` : "FIR Request") : (item.templateTitle || (item.templateID?.title || "N/A")))),
        reqDate: reqDateDisplay,
        engineer: engineerName,
        engineerContact: engineerContact,
        assignedVisitDate: visitedDateDisplay,
        status: displayStatus.toUpperCase(), // Use modified status for display
        originalStatus: item.status || item.projectStatus || item.firStatus || "UNKNOWN", // Keep original for internal logic
        _id: item._id,
        fieldInspectionSummary: item.fieldInspectionSummary || null,
        isFirRequest: isFirRequest,
        rawItem: item
      };
    });
  };

  // Helper function to format date
  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/')
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = totalProjects > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalProjects);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return selectedStatus !== "ALL" ||
      selectedSort !== "latest" ||
      sortConfig.column !== null ||
      dateFilter !== "ALL" ||
      selectedEngineers.length > 0 ||
      selectedClients.length > 0 ||
      selectedTemplates.length > 0 ||
      selectedStatuses.length > 0 ||
      searchQuery.trim() !== ""; // Add search query check
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedStatus("ALL");
    setSelectedSort("latest");
    setSortConfig({ column: null, direction: null });
    setDateFilter("ALL");
    setCustomDateRange({ from: null, to: null });
    setSelectedEngineers([]);
    setSelectedClients([]);
    setSelectedTemplates([]);
    setSelectedStatuses([]);
    setSearchQuery(""); // Clear search query
    setPage(1); // Reset to first page
  };

  // Clear search only
  const clearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Toggle filter section
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (!showFilters) {
      fetchFilterData();
    }
  };

  // Handle column sort click
  const handleColumnSort = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        // Toggle direction if same column
        return {
          column: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // New column, start with ascending
        return {
          column: column,
          direction: 'asc'
        };
      }
    });
  };

  // Get sort icon and color for a column
  const getSortIconForColumn = (column) => {
    if (sortConfig.column === column) {
      return sortConfig.direction === 'asc' ? 'foldUp' : 'foldDown';
    }
    return 'sort';
  };

  const getSortIconColorForColumn = (column) => {
    if (sortConfig.column === column) {
      return COLORS.PRIMARY;
    }
    return COLORS.LIGHT_SECONDARY;
  };

  // Handle view details
  const handleViewDetails = (project) => {
    const raw = project.rawItem || project;
    const targetProjectId = raw.associatedProject?._id || raw.associatedProject || (raw.source === "FIR" ? raw._id : null);

    // ONLY for Individual Client role, navigate to full project details
    if (hasRole("CLIENT") && isIndividual) {
      navigate(`/technical/project-details/${targetProjectId || project._id}`);
      return;
    }

    // For ALL OTHER roles (Engineers, Admin, Superadmin, and non-Individual Clients), open the details sidebar drawer
    setSelectedProject(project._id);
    setIsSidebarOpen(true);
  };

  // Handle edit - For ADMIN/SUPERADMIN and ENGINEER
  const handleEdit = async (projectId) => {
    if (hasRole("CLIENT") && isIndividual) {
       const targetProject = projects.find(p => p._id === projectId || p.id === projectId);
       if (targetProject) {
          navigate(`/admin/fir-requests/edit/${projectId}`, { state: { request: targetProject.firDetails || targetProject.rawItem || targetProject } });
       }
       return;
    }
    setSelectedProject(projectId);
    setIsEditMode(true);
    setIsEditScreenOpen(true);
    setHasEditChanges(false); // Reset changes flag when opening
  };

  // Handle changes detected in edit modal
  const handleEditChanges = () => {
    console.log("Edit changes detected!");

    setHasEditChanges(true);
  };

  // Handle delete - Only for ADMIN/SUPERADMIN
  const handleDelete = (project) => {
    setDeleteConfirm({ open: true, id: project.id, status: project.originalStatus || project.status });
  };

  const executeDelete = async () => {
    if (!isAuthenticated || !deleteConfirm.id) return;

    try {
      if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
        await axios.delete(`${url}/api/client/requests/${deleteConfirm.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success("Request deleted successfully");
        refreshUserData();
        fetchProjects();
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Failed to delete request");
    } finally {
      setDeleteConfirm({ open: false, id: null, status: null });
    }
  };

  // Handle download report - Only if a report exists for the project / FIR request
  const handleDownloadReport = async (projectId, projectItem = null) => {
    try {
      if (!token) {
        toast.error("Please login to download reports");
        return;
      }

      setDownloadProgress(prev => ({ ...prev, [projectId]: 0 }));

      const userRole = userData?.role || "CLIENT";
      console.log("Downloading report for project:", projectId, "as role:", userRole);
      
      const onDownloadProgress = (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setDownloadProgress(prev => ({ ...prev, [projectId]: percentCompleted }));
        } else {
          // Fallback if no total size provided
          setDownloadProgress(prev => ({ ...prev, [projectId]: 50 })); // Indeterminate-like
        }
      };

      // 1. If it is an FIR request / FIR project, try downloading aggregated PDF first
      if (projectItem?.isFirRequest || projectItem?.rawItem?.source === "FIR" || projectItem?.rawItem?.firStatus) {
        try {
          const response = await axios.post(
            `${url}/api/field-inspection-reports/download-aggregated`,
            { projectId: projectId },
            {
              headers: { Authorization: `Bearer ${token}` },
              responseType: 'blob',
              timeout: 900000, // 15 min timeout for large PDF generation
              onDownloadProgress
            }
          );

          const contentType = response.headers["content-type"] || "";
          if (!contentType.includes("application/json")) {
            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `FIR_${projectItem?.projectName || 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            toast.success("Report downloaded successfully");
            setDownloadProgress(prev => { const next = { ...prev }; delete next[projectId]; return next; });
            return;
          }
        } catch (firErr) {
          console.warn("Aggregated FIR report download fallback:", firErr);
        }
      }

      // 2. Fallback / Standard checklist report download
      const response = await axios.get(
        `${url}/api/admin/download-checklist-report/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            userRole: userRole
          },
          responseType: 'blob',
          timeout: 900000, // 15 min timeout for large PDF generation
          onDownloadProgress
        }
      );

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        let filename = null;
        const match = contentDisposition.match(/filename="([^"]+)"/i);
        if (match && match[1]) {
          filename = match[1];
        } else {
          const match2 = contentDisposition.match(/filename=([^;]+)/i);
          if (match2 && match2[1]) filename = match2[1].trim();
        }

        if (filename) {
          link.download = filename;
        } else {
          link.download = `checklist-report-${projectId}.pdf`;
        }
      } else {
        link.download = `checklist-report-${projectId}.pdf`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Report downloaded successfully");

    } catch (err) {
      console.error("Error downloading report:", err);
      toast.error(err.response?.data?.message || "Failed to download report");
    } finally {
      setDownloadProgress(prev => { const next = { ...prev }; delete next[projectId]; return next; });
    }
  };

  // Handle add new site check
  const handleAddNewSiteCheck = () => {
    navigate("/projects/add-site-check");
  };



  // Handle calendar filter selection
  const handleCalendarFilterSelect = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowDatePicker(true);
      setCalendarAnchorEl(null);
    } else {
      setDateFilter(filterValue);
      setCalendarAnchorEl(null);
    }
  };

  // Handle custom date range selection
  const handleCustomDateRangeSelect = () => {
    if (customDateRange.from && customDateRange.to) {
      setDateFilter("CUSTOM");
      setShowDatePicker(false);
    }
  };

  // Define table headers with role-based columns
  const getTableHeaders = () => {
    const headers = [
      { key: "Project Name", hasSort: true, sortKey: "projectName", width: "160px" },
      { key: "Client Name", hasSort: true, sortKey: "clientName", width: "130px" },
    ];

    // Add Engineer column for ADMIN, SUPERADMIN, and CLIENT
    if (hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("CLIENT")) {
      headers.push({ key: "Engineer", hasSort: false, width: "120px" });
    }

    if (hasRole("CLIENT") && isIndividual) {
      headers.push({ key: "Property Type", hasSort: false, width: "100px" });
    } else {
      headers.push({ key: "Site Check", hasSort: false, width: "100px" });
    }

    headers.push(
      { key: "Req Date", hasSort: false, width: "80px" }
    );

    if (!(hasRole("CLIENT") && isIndividual)) {
      headers.push({ key: "Visit Date", hasSort: true, sortKey: "visitDate", width: "100px" });
    }

    headers.push(
      { key: "Status", hasSort: false, width: "110px" } // increased width to fit status properly
    );

    
    headers.push({ key: "Actions", hasSort: false, width: "100px" });
    return headers;
  };

  // Get available statuses based on role
  // Replace the current getAvailableStatuses function with this:
  const getAvailableStatuses = () => {
    const allStatuses = [
      { label: "Unassigned", value: "UNASSIGNED" },
      { label: "Assigned", value: "ASSIGNED" },
      { label: "Visited", value: "VISITED" },
      { label: "Sign Pending", value: "SIGNPENDING" },
      { label: "Final Report", value: "FINALREPORT" },
      { label: "Rejected", value: "REJECTED" },
    ];

    // ENGINEER: Cannot see UNASSIGNED and VISITED
    if (hasRole("ENGINEER")) {
      return allStatuses.filter(status =>
        !["UNASSIGNED", "VISITED"].includes(status.value)
      );
    }

    // ADMIN/SUPERADMIN: Cannot see VISITED
    if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
      return allStatuses.filter(status =>
        !["VISITED"].includes(status.value)
      );
    }

    // CLIENT: Cannot see SIGNPENDING (sees VISITED instead)
    if (hasRole("CLIENT")) {
      return allStatuses.filter(status =>
        !["SIGNPENDING"].includes(status.value)
      );
    }

    // Default fallback
    return allStatuses;
  };
  // Show message if user doesn't have access
  if (!isAuthenticated || !(hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          This page is only accessible to CLIENT, ADMIN, SUPERADMIN, or ENGINEER users.
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}
            >
              {hasRole("ENGINEER") ? "My Assigned Projects" :
                hasRole("CLIENT") ? (isIndividual ? "My Requests" : "My Site Check Projects") :
                  "All Site Check Projects"}
            </Typography>

            {hasRole("CLIENT") && !isIndividual && (
              <Button
                startIcon={<Icon name="plus2" />}
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: 2,
                  ...TYPOGRAPHY.H4,
                  px: 2.5,
                  "&:hover": { bgcolor: COLORS.SECONDARY },
                }}
                onClick={handleAddNewSiteCheck}
              >
                Add New Site Check
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexGrow: 0,
                width: { xs: 180, sm: 250, md: 280 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1); // Reset to first page when searching
                }}
                sx={{
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                  mt: 0.5,
                  '& input::placeholder': {
                    fontSize: '18px',   // increase size here
                    // optional
                  },
                }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Calendar Menu - Show active filter */}
            {!(hasRole("CLIENT") && isIndividual) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CustomTooltip title="Calendar Filter">
                  <IconButton
                    onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                    sx={{
                      color: dateFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                    }}
                  >
                    <Icon name="calendar2" />
                  </IconButton>
                </CustomTooltip>
              </Box>
            )}


            {/* Sort Menu - Show active filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Sort Options">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    color: selectedSort !== "latest" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="sort" />
                </IconButton>
              </CustomTooltip>
            </Box>
            {/* Filter Button - Toggles filter section */}
            {!(hasRole("CLIENT") && isIndividual) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CustomTooltip title="Show Filters">
                  <IconButton
                    onClick={toggleFilters}
                    sx={{
                      color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                    }}
                  >
                    <Icon name="list" />
                  </IconButton>
                </CustomTooltip>
              </Box>
            )}


            {isFilterApplied() && (
              <CustomTooltip title="Clear Filters">
                <IconButton onClick={clearFilters}>
                  <Icon name="rotate" color={COLORS.ERROR} />
                </IconButton>
              </CustomTooltip>
            )}
          </Box>
        </Box>


        {/* Filter Section - Horizontal Boxes (Only shown when showFilters is true) */}
        {showFilters && !(hasRole("CLIENT") && isIndividual) && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Engineer Filter (only for ADMIN/SUPERADMIN) */}
            {/* Engineer Filter */}
            {/* Engineer Filter */}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Engineer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedEngineers}
                    onChange={(e) => {
                      setSelectedEngineers(e.target.value);
                      setPage(1);
                      setTimeout(() => {
                        setFilterMenusOpen(prev => ({ ...prev, engineers: false }));
                      }, 100);
                    }}
                    open={filterMenusOpen.engineers}
                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, engineers: true }))}
                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, engineers: false }))}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Engineers
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          width: 280,
                          overflow: 'auto',
                          mt: 0.5,
                          '& .MuiMenuItem-root': {
                            minHeight: 40,
                            padding: '6px 12px',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '6px',
                          }
                        }
                      },
                    }}
                  >
                    {loadingFilters.engineers ? (
                      <MenuItem disabled sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : engineers.length > 0 ? (
                      engineers.map((engineer) => (
                        <MenuItem
                          key={engineer._id}
                          value={engineer.name}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedEngineers.indexOf(engineer.name) > -1}
                            size="small"
                          />
                          <ListItemText
                            primary={engineer.name}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                                fontWeight: 400,
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No engineers available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Client Filter (for ADMIN/SUPERADMIN and ENGINEER, CLIENT sees only their own) */}
            {/* Client Filter */}
            {/* Client Filter */}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER")) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Client
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedClients}
                    onChange={(e) => {
                      setSelectedClients(e.target.value);
                      setPage(1);
                      setTimeout(() => {
                        setFilterMenusOpen(prev => ({ ...prev, clients: false }));
                      }, 100);
                    }}
                    open={filterMenusOpen.clients}
                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, clients: true }))}
                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, clients: false }))}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Clients
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          width: 280,
                          overflow: 'auto',
                          mt: 0.5,
                          '& .MuiMenuItem-root': {
                            minHeight: 40,
                            padding: '6px 12px',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '6px',
                          }
                        }
                      },
                    }}
                  >
                    {loadingFilters.clients ? (
                      <MenuItem disabled sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : clients.length > 0 ? (
                      clients.map((client) => (
                        <MenuItem
                          key={client._id}
                          value={client.name || client.companyName}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedClients.indexOf(client.name || client.companyName) > -1}
                            size="small"
                          />
                          <ListItemText
                            primary={client.name || client.companyName}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                                fontWeight: 400,
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No clients available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Site Check Type Filter (for all roles) */}
            {/* Site Check Type Filter */}
            {/* Site Check Type Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Site Check Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedTemplates}
                  onChange={(e) => {
                    setSelectedTemplates(e.target.value);
                    setPage(1);
                    setTimeout(() => {
                      setFilterMenusOpen(prev => ({ ...prev, templates: false }));
                    }, 100);
                  }}
                  open={filterMenusOpen.templates}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, templates: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, templates: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Site Check Types
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,  // Reduced height
                        width: 280,
                        overflow: 'auto',
                        marginTop: '4px', // Position below filter
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        '& .MuiMenuItem-root': {
                          minHeight: 36,  // Reduced item height
                          padding: '4px 12px',
                          fontSize: '0.85rem',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px 6px 4px 4px',
                        }
                      }
                    },
                  }}
                >
                  {loadingFilters.templates ? (
                    <MenuItem disabled sx={{ justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={18} />
                    </MenuItem>
                  ) : templates.length > 0 ? (
                    templates.map((template) => (
                      <MenuItem
                        key={template._id}
                        value={template.title}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <Checkbox
                          checked={selectedTemplates.indexOf(template.title) > -1}
                          size="small"
                          sx={{ '& svg': { fontSize: '0.9rem' } }}
                        />
                        <ListItemText
                          primary={template.title}
                          sx={{
                            margin: 0,
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              lineHeight: 1.2,
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ py: 1 }}>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: COLORS.TEXT,
                        fontStyle: 'italic'
                      }}>
                        No templates available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Status Filter (for all roles) */}
            {/* Status Filter */}
            {/* Status Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={(e) => {
                    setSelectedStatuses(e.target.value);
                    setPage(1);
                    setTimeout(() => {
                      setFilterMenusOpen(prev => ({ ...prev, statuses: false }));
                    }, 100);
                  }}
                  open={filterMenusOpen.statuses}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, statuses: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, statuses: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Statuses
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(s => s.charAt(0) + s.slice(1).toLowerCase()).join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {getAvailableStatuses().map((status) => (
                    <MenuItem
                      key={status.value}
                      value={status.value}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedStatuses.indexOf(status.value) > -1}
                        size="small"
                      />
                      <ListItemText
                        primary={status.label}
                        sx={{
                          margin: 0,
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                            fontWeight: 400,
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search Results Info */}
        {/* {searchQuery && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY }}>
              Search results for: <strong>"{searchQuery}"</strong>
            </Typography>
            <Button 
              size="small" 
              onClick={clearSearch}
              sx={{ 
                textTransform: 'none',
                color: COLORS.ERROR,
                fontSize: '0.75rem'
              }}
            >
              Clear search
            </Button>
          </Box>
        )} */}

        {/* Table Container */}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: '4px',
          },
        }}>
          {/* Table */}
          <Paper elevation={0} sx={{
            backgroundColor: "transparent",
            minWidth: '1000px' // Reduced from 1200px to make table fit better
          }}>
            <TableContainer>
              <Table
                sx={{
                  backgroundColor: "transparent",
                  "& thead th": {
                    backgroundColor: "transparent",
                    padding: "8px 6px", // Reduced padding
                  },
                  "& tbody td": {
                    backgroundColor: "transparent",
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    padding: "8px 6px", // Reduced padding
                    verticalAlign: 'top',
                  },
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <TableHead>
                  <TableRow>
                    {getTableHeaders().map((head) => (
                      <TableCell
                        key={head.key}
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          width: head.width,
                          minWidth: head.width,
                          maxWidth: head.width,
                          padding: "8px 6px", // Reduced padding
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                          }}
                        >
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {head.key}
                          </span>
                          {head.hasSort && (
                            <CustomTooltip title={`Sort by ${head.key}`}>
                              <Icon
                                name={getSortIconForColumn(head.sortKey)}
                                color={getSortIconColorForColumn(head.sortKey)}
                                sx={{
                                  fontSize: 14,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  "&:hover": {
                                    color: COLORS.PRIMARY,
                                  },
                                }}
                                onClick={() => handleColumnSort(head.sortKey)}
                              />
                            </CustomTooltip>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: COLORS.TEXT }}>
                          {!isFilterApplied()
                            ? (hasRole("CLIENT") ? "You haven't created any site check requests yet." :
                              hasRole("ENGINEER") ? "No projects assigned to you yet." :
                                "No projects found.")
                            : `No projects found with selected filters`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects.map((project) => (
                      <TableRow key={project.id} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                        {/* Project Name Column */}
                        <TableCell sx={{
                          width: '160px',
                          minWidth: '160px',
                          maxWidth: '160px',
                          padding: "8px 6px"
                        }}>
                          {/* Report Number Badge - Only show for FINALREPORT status */}
                          {project.status === "FINALREPORT" && project.projectReportNo && project.projectReportNo !== "" ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                border: `1.5px solid ${COLORS.PRIMARY}`,
                                backgroundColor: COLORS.SECONDARY,
                                color: COLORS.BACKGROUND,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                marginBottom: '6px',
                                width: 'fit-content',
                                minWidth: '60px',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}
                            >
                              {project.projectReportNo}
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.PRIMARY,
                                fontSize: "1rem",
                                marginBottom: '6px',
                                fontWeight: 600
                              }}
                            >

                            </Typography>
                          )}
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.rawItem?.source === "FIR" || project.isFirRequest || project.source === "FIR" || project.firDetails
                              ? (project.propertyName || project.firDetails?.propertyName || project.projectName || project.firDetails?.projectName || project.buildingName || project.firDetails?.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                              : (project.projectName || project.propertyName || project.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                            }
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            {project.clientAddress}
                          </Typography>
                        </TableCell>

                        {/* Client Name Column */}
                        <TableCell sx={{
                          width: '130px',
                          minWidth: '130px',
                          maxWidth: '130px',
                          padding: "8px 6px" // Reduced padding
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.clientName}
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            {project.clientContact}
                          </Typography>
                        </TableCell>

                        {/* Engineer Column (for ADMIN/SUPERADMIN and CLIENT) */}
                        {(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("CLIENT")) && (
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                            padding: "8px 6px"
                          }}>
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_LARGE,
                                color: project.engineer === "Not Assigned" ? COLORS.MUTED_TEXT : COLORS.TEXT,
                                fontSize: "0.8rem",
                                wordBreak: 'break-word',
                                lineHeight: 1.3,
                                mb: 0.5,
                                fontStyle: project.engineer === "Not Assigned" ? "italic" : "normal",
                              }}
                            >
                              {project.engineer}
                            </Typography>
                            {project.engineerContact && !hasRole("CLIENT") && (
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_SMALL,
                                  color: COLORS.TEXT,
                                  fontSize: "0.7rem",
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2
                                }}
                              >
                                {project.engineerContact}
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        {/* Site Check Column */}
                        <TableCell
                          sx={{
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px',
                            padding: "8px 6px", // Reduced padding
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.siteCheck}
                        </TableCell>

                        {/* Request Date Column */}
                        <TableCell
                          sx={{
                            width: '80px',
                            minWidth: '80px',
                            maxWidth: '80px',
                            padding: "8px 6px", // Reduced padding
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.reqDate}
                        </TableCell>

                        {/* Visit Date Column - Hidden for Individual Clients */}
                        {!(hasRole("CLIENT") && isIndividual) && (
                          <TableCell
                            sx={{
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px',
                              padding: "8.8px 6.8px", // Reduced padding
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            {project.assignedVisitDate}
                          </TableCell>
                        )}
                        <TableCell sx={{
                          width: '110px',
                          minWidth: '110px',
                          maxWidth: '110px',
                          padding: "8px 6px" // Reduced padding
                        }}>
                          <Chip
                            label={project.status}
                            size="small"
                            sx={{
                              ...(statusStyles[project.status] || {}),
                              height: 'auto',
                              py: 0.5,
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontSize: "0.65rem",
                              textTransform: 'capitalize',
                              whiteSpace: 'normal',
                              textAlign: 'center',
                              '& .MuiChip-label': {
                                padding: '0 6px',
                                display: 'block',
                                whiteSpace: 'normal',
                              }
                            }}
                          />
                        </TableCell>



                        {/* Actions Column */}
                        <TableCell sx={{
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px',
                          padding: "8px 6px" // Reduced padding
                        }}>
                          <Box sx={{ display: "flex", gap: 0.25, flexWrap: 'wrap' }}>
                            {/* View Details - Always visible for all roles */}
                            <CustomTooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(project)}
                                sx={{
                                  padding: "2px",
                                  minWidth: '24px',
                                  height: '24px'
                                }}
                              >
                                <Icon name="Action1" color={COLORS.TEXT} />
                              </IconButton>
                            </CustomTooltip>

                            {/* Edit - For ADMIN/SUPERADMIN and UNASSIGNED CLIENT */}
                            {(hasRole("ADMIN") || hasRole("SUPERADMIN") || (hasRole("CLIENT") && project.engineer === "Not Assigned")) && (
                              <CustomTooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(project._id || project.id)}
                                  sx={{
                                    padding: "2px",
                                    minWidth: '24px',
                                    height: '24px'
                                  }}
                                >
                                  <Icon name="Action3" color={COLORS.TEXT} />
                                </IconButton>
                              </CustomTooltip>
                            )}

                            {/* Delete - Only for ADMIN/SUPERADMIN */}
                            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                              <CustomTooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(project)}
                                  sx={{
                                    padding: "2px",
                                    minWidth: '24px',
                                    height: '24px'
                                  }}
                                >
                                  <Icon name="Action4" color={COLORS.ERROR} />
                                </IconButton>
                              </CustomTooltip>
                            )}

                            {/* Download Report - Displayed in Actions section */}
                            {(hasRole("CLIENT") || hasRole("ENGINEER") || hasRole("ADMIN") || hasRole("SUPERADMIN")) && project.status === "REPORT SUBMITTED" && (
                                <CustomTooltip title="Download Report">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadReport(project._id || project.id, project)}
                                    disabled={downloadProgress[project._id || project.id] !== undefined}
                                    sx={{
                                      padding: "1.3px",
                                      minWidth: '20px',
                                      height: '20px',
                                      mt: 0.18
                                    }}
                                  >
                                    {downloadProgress[project._id || project.id] !== undefined ? (
                                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress variant={downloadProgress[project._id || project.id] > 0 && downloadProgress[project._id || project.id] < 100 ? "determinate" : "indeterminate"} value={downloadProgress[project._id || project.id]} size={18} color="inherit" />
                                        {downloadProgress[project._id || project.id] > 0 && downloadProgress[project._id || project.id] < 100 && (
                                          <Box sx={{
                                            top: 0, left: 0, bottom: 0, right: 0,
                                            position: 'absolute',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}>
                                            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '7px', fontWeight: 'bold' }}>
                                              {`${downloadProgress[project._id || project.id]}%`}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    ) : (
                                      <Icon name="download1" color={COLORS.SECONDARY} />
                                    )}
                                  </IconButton>
                                </CustomTooltip>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && projects.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  mt: 2,

                  width: '100%',
                  overflowX: 'auto',
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                  {startIndex} - {endIndex} of {totalProjects} projects
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-left" size={18} />
                  </IconButton>

                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (page === 1) {
                      pageNum = i + 1;
                    } else if (page === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = page - 1 + i;
                    }

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 24,
                            height: 24,
                            fontSize: "0.7rem",
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                            color: page === pageNum ? "#fff" : COLORS.TEXT,
                            border: page === pageNum ? "none" : "1px solid #E1BEE7",
                            "&:hover": {
                              bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                            },
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                    return null;
                  })}

                  <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-right" size={18} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "Latest First", value: "latest", icon: "foldUp" },
            { label: "Oldest First", value: "oldest", icon: "foldDown" },
            { label: "A to Z", value: "AZ", icon: "foldUp" },
            { label: "Z to A", value: "ZA", icon: "foldDown" },
          ].map((item) => {
            const isActive = selectedSort === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => {
                  setSelectedSort(item.value);
                  // Clear column sort when global sort is selected
                  setSortConfig({ column: null, direction: null });
                  setSortAnchorEl(null);
                }}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  "&.Mui-selected": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Calendar Menu with Icons */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Projects", value: "ALL", icon: "calendarAll" },
            { label: "Today", value: "TODAY", icon: "calendarToday" },
            { label: "Yesterday", value: "YESTERDAY", icon: "calendarYesterday" },
            { label: "Tomorrow", value: "TOMORROW", icon: "calendarTomorrow" },
            { label: "Custom Date Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = dateFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleCalendarFilterSelect(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Custom Date Range Picker Dialog */}
        <Dialog
          open={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Date Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customDateRange.from}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, from: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={customDateRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customDateRange.to}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, to: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={customDateRange.from}
                maxDate={new Date()}
              />

            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowDatePicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomDateRangeSelect}
              disabled={!customDateRange.from || !customDateRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Date Range
            </Button>
          </DialogActions>
        </Dialog>


        <ClientDetailsSidebar
          open={isSidebarOpen}
          onClose={() => {
            setIsSidebarOpen(false);
            setSelectedProject(null);
          }}
          projectId={selectedProject} // Pass ID here
        />

        {/* {isEditScreenOpen && (
          <EditChecklistSidebarWithToast
            open={isEditScreenOpen}
            onClose={() => {
              console.log("Edit modal closing. hasEditChanges:", hasEditChanges);
              setIsEditScreenOpen(false);
              setSelectedProject(null);
              setIsEditMode(false);
              // Only fetch projects if changes were made
              if (isEditMode && hasEditChanges) {
                console.log("Fetching projects due to changes detected");
                fetchProjects();
              } else {
                console.log("No changes detected, not fetching projects");
              }
            }}
            projectId={selectedProject}
            mode={isEditMode ? "edit" : "view"}
            onChangesDetected={handleEditChanges}
          />
        )} */}

        {isEditScreenOpen && (
          <EditChecklistSidebarWithToast
            open={isEditScreenOpen}
            onClose={() => {
              console.log("Edit modal closing. hasEditChanges:", hasEditChanges);
              // Save the change state before closing
              const hadChanges = hasEditChanges;

              setIsEditScreenOpen(false);
              setSelectedProject(null);
              setIsEditMode(false);

              // Always reset change tracking
              setHasEditChanges(false);

              // Refresh if there were changes
              if (hadChanges) {
                console.log("Changes detected, refreshing projects...");
                fetchProjects();
              } else {
                console.log("No changes detected, not refreshing.");
              }
            }}
            projectId={selectedProject}
            mode={isEditMode ? "edit" : "view"}
            onChangesDetected={handleEditChanges}
          />
        )}
        <ConfirmDialog
          open={deleteConfirm.open}
          title="Confirm Delete"
          message={
            deleteConfirm.status && ["ASSIGNED", "VISITED", "SIGNPENDING", "FINALREPORT"].includes(deleteConfirm.status)
              ? `This project is already marked as ${statusDisplayNames[deleteConfirm.status] || deleteConfirm.status}. Are you sure you want to delete this request? This action cannot be undone.`
              : "Are you sure you want to delete this request? This action cannot be undone."
          }
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: null, status: null })}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AllProjects;